import {
  ApiTags,
  ApiGeneralDataResponse,
  ApiResponse,
  BaseBotRequest,
  BasePublicBotRequest,
  BotData,
  BotGeneralData,
  BotSymbolPriceData,
  BotTemplateData,
  ChangeBotPublicityRequest,
  CreateBotRequest,
  EditBotRequest,
  GetBotGraphDataRequest,
  GetPublicBotGraphDataRequest,
  GetPublicBotsRequest,
  PublicBotData,
  UpdatedRawChartData,
  PublicBotsDataByTag,
  BulkChangeBotsStatusRequest,
  ExchangeToSymbolPrices,
  EditBotsRequest,
  GetBotCreateGeneralDataRequest,
  GetSubGroupBotConfigurationsRequest,
  StrippedBotData,
  CalculatePriceDifferenceRequest,
  CalculatePriceDifferenceResponse,
  BotMetadata,
  GetBotBulkCreateGeneralDataRequest,
  BotsBulkGeneralData,
  OneClickFollowBotData,
  StartBacktestingRunRequest,
  GetBacktestingRunsRequest,
  GetBacktestingRunsResponse,
  GetBotsNamesRequest,
  BacktestingRunType,
  GetGroupMainCoinsRequest,
  GetGroupMainCoinsResponse,
  BacktestingActivity,
  DCABotPageSource,
  PeriodicStatsDataPoint,
  BotType,
  EditBotConfigurationRequest,
  GetMinimumTimestampRequest,
  GetGroupMainCoinsMultipleExchangesRequest,
} from "@shared/types";
import moment from "moment";
import { EndpointBuilder } from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import {
  dcaBotBacktestingFilters,
  groupConfigurationsFilters,
  publicBotsFilters,
  signalsBotBacktestingFilters,
} from "@shared/slices/filters.slice";
import { getEditBotsInvalidateTags } from "@shared/features/bots/utils";
import { getJobQuery } from "@shared/services/jobs/utils";
import { INITIAL_DATA_MAX_TIME_SECONDS } from "@shared/common/constants";
import { BOT_TYPE_TO_FILTERS_SLICE } from "@shared/features/bots/constants";
import { convertToObjectsArray } from "@shared/utils/arrays";

export const botsEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    getBotCreateGeneralData: build.query<
      BotGeneralData,
      GetBotCreateGeneralDataRequest
    >({
      query: (args) => ({
        url: "get_bot_create_general_data/",
        method: "POST",
        body: { ...args, include_without: false },
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<BotGeneralData>>
      ) => {
        const data = response?.data?.data || {};
        return { ...data, id_to_value: response?.data.id_to_value };
      },
      keepUnusedDataFor: 0,
    }),
    getBotBulkCreateGeneralData: build.query<
      BotsBulkGeneralData,
      GetBotBulkCreateGeneralDataRequest
    >({
      query: (args) => ({
        url: "get_bot_bulk_create_general_data/",
        method: "POST",
        body: { ...args, include_without: false },
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<BotsBulkGeneralData>>
      ) => {
        const data = response?.data?.data || {};
        return { ...data, id_to_value: response?.data.id_to_value };
      },
      keepUnusedDataFor: 0,
    }),
    getBotData: build.query<ApiGeneralDataResponse<BotData>, BaseBotRequest>({
      query: (args) => ({
        url: "get_bot_data/",
        method: "POST",
        body: { ...args, include_without: false },
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<BotData>>
      ) => {
        return response?.data;
      },
      providesTags: (_, __, args) => [
        { type: ApiTags.BOTS, id: args.dca_bot_id },
      ],
      keepUnusedDataFor: 0,
    }),
    getPublicBotData: build.query<PublicBotData, BasePublicBotRequest>({
      query: (args) => ({
        url: "get_public_bot/",
        method: "POST",
        body: { ...args, include_without: false },
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<PublicBotData>>
      ) => {
        return response?.data?.data || {};
      },
      keepUnusedDataFor: 0,
    }),
    getBotsData: getJobQuery({
      build,
      endpointName: "getBotsDataJob",
      transformResponse: (response) => {
        return {
          ...response?.data,
          data: convertToObjectsArray(
            response?.data.data,
            response.data.headers
          ),
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        if (args.bot_page_source !== DCABotPageSource.CREATE_FORM) {
          try {
            const filtersSlice = BOT_TYPE_TO_FILTERS_SLICE[args.bot_type];
            const { data } = await queryFulfilled;
            dispatch(filtersSlice.actions.loadFilters({ data }));
          } catch {}
        }
      },
      getArgs: (args) => ({
        ...args,
        return_list: true,
        include_without: false,
      }),
      providesTags: () => [{ type: ApiTags.BOTS, id: "LIST" }],
      keepUnusedDataFor: INITIAL_DATA_MAX_TIME_SECONDS,
    }),
    getBotsGraphsData: getJobQuery({
      build,
      endpointName: "getBotsGraphsDataJob",
      transformResponse: (response) => {
        return response?.data;
      },
      getArgs: (args) => ({
        ...args,
        timezone_difference: moment().utcOffset(),
      }),
    }),
    createBots: build.mutation<BotData[], CreateBotRequest>({
      query: (args) => ({
        url: "create_bots/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<BotData[]>) => {
        return response?.data;
      },
      invalidatesTags: (_, __, args) => {
        const tags = [
          { type: ApiTags.BOTS, id: "LIST" },
          { type: ApiTags.MAIN_COINS_AVAILABLE_AMOUNT, id: "LIST" },
        ];
        if (args.bot_type === BotType.SIGNAL) {
          tags.push({ type: ApiTags.SIGNALS, id: "LIST" });
        }
        return tags;
      },
    }),
    editBot: build.mutation<BotData, EditBotRequest>({
      query: (args) => ({
        url: "edit_bot/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<BotData>) => {
        return response?.data;
      },
      invalidatesTags: (_, error, args) => {
        const tags = error ? [] : getEditBotsInvalidateTags([args.dca_bot_id]);
        if (args.bot_type === BotType.SIGNAL) {
          tags.push({ type: ApiTags.SIGNALS, id: "LIST" });
        }
        return tags;
      },
    }),
    editBots: build.mutation<BotData, EditBotsRequest>({
      query: (args) => ({
        url: "edit_bots/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<BotData>) => {
        return response?.data;
      },
      invalidatesTags: (_, error, args) =>
        error ? [] : getEditBotsInvalidateTags(args.bot_ids),
    }),
    deleteBots: build.mutation<void, { bot_ids: number[] }>({
      query: (args) => ({
        url: "delete_bots/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [
        { type: ApiTags.BOTS, id: "LIST" },
        { type: ApiTags.MAIN_COINS_AVAILABLE_AMOUNT, id: "LIST" },
      ],
    }),
    bulkChangeBotsStatus: build.mutation<void, BulkChangeBotsStatusRequest>({
      query: (args) => ({
        url: "bulk_change_bots_status/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [
        { type: ApiTags.BOTS, id: "LIST" },
        { type: ApiTags.MAIN_COINS_AVAILABLE_AMOUNT, id: "LIST" },
      ],
    }),
    changeBotPublicity: build.mutation<void, ChangeBotPublicityRequest>({
      query: (args) => ({
        url: "change_bot_publicity/",
        method: "POST",
        body: args,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            sharedLib.sharedApi.bots.util.updateQueryData(
              "getBotData",
              { dca_bot_id: args.dca_bot_id, is_channel_configuration: false },
              (botData) => {
                botData.data.is_public = args.is_public;
              }
            )
          );
        } catch {}
      },
    }),
    deleteBot: build.mutation<void, BaseBotRequest>({
      query: (args) => ({
        url: "delete_bot/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [{ type: ApiTags.BOTS, id: "LIST" }],
    }),
    getBotGraphData: build.query<
      ApiGeneralDataResponse<UpdatedRawChartData>,
      GetBotGraphDataRequest
    >({
      query: (args) => ({
        url: "get_bot_graph_data/",
        method: "POST",
        body: { ...args, timezone_difference: moment().utcOffset() },
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<UpdatedRawChartData>>
      ) => {
        return response?.data;
      },
      keepUnusedDataFor: 0,
    }),
    getPublicBotGraphData: build.query<
      ApiGeneralDataResponse<UpdatedRawChartData>,
      GetPublicBotGraphDataRequest
    >({
      query: (args) => ({
        url: "get_public_bot_graph_data/",
        method: "POST",
        body: { ...args, timezone_difference: moment().utcOffset() },
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<UpdatedRawChartData>>
      ) => {
        return response?.data;
      },
      keepUnusedDataFor: 0,
    }),
    getPublicBots: build.query<
      ApiGeneralDataResponse<BotTemplateData[]>,
      GetPublicBotsRequest
    >({
      query: (args) => ({
        url: "get_public_bots/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<BotTemplateData[]>>
      ) => {
        return response?.data;
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(publicBotsFilters.actions.loadFilters({ data }));
        } catch {}
      },
    }),
    getPublicBotsByTag: build.query<
      ApiGeneralDataResponse<PublicBotsDataByTag>,
      GetPublicBotsRequest
    >({
      query: (args) => ({
        url: "get_public_bots/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<PublicBotsDataByTag>>
      ) => {
        return response?.data;
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(publicBotsFilters.actions.loadFilters(data));
        } catch {}
      },
    }),
    getAccountsMainCoinsAvailableAmount: getJobQuery({
      build,
      endpointName: "getAccountsMainCoinsAvailableAmountJob",
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: () => [
        { type: ApiTags.MAIN_COINS_AVAILABLE_AMOUNT, id: "LIST" },
      ],
      keepUnusedDataFor: 0,
    }),
    getAccountsMainCoinsTotalAndAvailableAmount: getJobQuery({
      build,
      endpointName: "getAccountsMainCoinsTotalAndAvailableAmountJob",
      transformResponse: (response) => {
        return response?.data;
      },
    }),
    getExchangeSymbolPrice: build.query<
      BotSymbolPriceData,
      { exchange: string; symbol: string }
    >({
      query: (args) => ({
        url: "get_exchange_symbol_price/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<BotSymbolPriceData>) => {
        return response?.data;
      },
    }),
    getExchangeToSymbolPrices: build.query<
      ExchangeToSymbolPrices,
      { exchange_to_symbols: Record<string, string[]> }
    >({
      query: (args) => ({
        url: "get_exchange_to_symbol_prices/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<ExchangeToSymbolPrices>>
      ) => {
        return response?.data.data;
      },
    }),
    getBotMetadata: build.query<BotMetadata, BaseBotRequest>({
      query: (args) => ({
        url: "get_bot_metadata/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<any>>
      ) => {
        return response?.data.data;
      },
      keepUnusedDataFor: 0,
    }),
    getBotsNames: build.query<Record<number, string>, GetBotsNamesRequest>({
      query: (args) => ({
        url: "get_bots_names/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<Record<number, string>>>
      ) => {
        return response?.data.data;
      },
      keepUnusedDataFor: 0,
    }),
    getSubGroupBotConfigurations: build.query<
      ApiGeneralDataResponse<(BotData | StrippedBotData)[]>,
      GetSubGroupBotConfigurationsRequest
    >({
      query: (args) => ({
        url: "get_sub_group_bot_configurations/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<BotData[]>>
      ) => {
        return response?.data;
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            groupConfigurationsFilters.actions.loadFilters({
              data,
              forceLoad: true,
            })
          );
        } catch {}
      },
      keepUnusedDataFor: 0,
    }),
    calculatePriceDifference: build.query<
      number,
      CalculatePriceDifferenceRequest
    >({
      query: (args) => ({
        url: "calculate_price_difference/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<
          ApiGeneralDataResponse<CalculatePriceDifferenceResponse>
        >
      ) => {
        return response?.data.data.price_difference * 100;
      },
    }),
    getSubGroupDefaultConfiguration: build.query<
      BotData,
      { sub_group_id: number; exchange: string }
    >({
      query: (args) => ({
        url: "get_sub_group_default_configuration/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<BotData>>
      ) => {
        return response?.data?.data;
      },
      providesTags: (_, __, args) => [
        {
          type: ApiTags.CHANNEL_CONFIGURATION,
          id: `${args.sub_group_id}_${args.exchange}`,
        },
      ],
      keepUnusedDataFor: 0,
    }),
    setSubGroupDefaultConfiguration: build.mutation<
      void,
      EditBotConfigurationRequest
    >({
      query: (args) => ({
        url: "set_sub_group_default_configuration/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (_, error, args) => {
        if (error) return [];
        return [
          {
            type: ApiTags.CHANNEL_CONFIGURATION,
            id: `${args.sub_group_ids?.[0]}_${args.exchange}`,
          },
        ];
      },
    }),
    setDefaultOneClickSignalsBot: build.mutation<
      void,
      { bot_id: number; should_set_default: boolean }
    >({
      query: (args) => ({
        url: "set_default_one_click_signals_bot/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [{ type: ApiTags.BOTS, id: "LIST" }],
    }),
    getBotsOneClickData: build.query<OneClickFollowBotData[], void>({
      query: (args) => ({
        url: "get_bots_one_click_data/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<OneClickFollowBotData[]>>
      ) => {
        return response?.data.data;
      },
      keepUnusedDataFor: 0,
    }),
    startBacktestingRun: build.mutation<
      BacktestingRunType,
      StartBacktestingRunRequest
    >({
      query: (args) => ({
        url: "start_backtesting_run/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<BacktestingRunType>>
      ) => {
        return response?.data.data;
      },
    }),
    deleteBacktestingRun: build.mutation<void, { backtesting_run_id: number }>({
      query: (args) => ({
        url: "delete_backtesting_run/",
        method: "POST",
        body: args,
      }),
    }),
    getBacktestingRuns: build.query<
      GetBacktestingRunsResponse,
      GetBacktestingRunsRequest | void
    >({
      query: (args) => ({
        url: "get_backtesting_runs/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<GetBacktestingRunsResponse>
      ) => {
        return response?.data;
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        if (args && Object.keys(args).length === 1 && args.bot_type) {
          try {
            const { data } = await queryFulfilled;
            const filtersSlice =
              args.bot_type === BotType.SIGNAL
                ? signalsBotBacktestingFilters
                : dcaBotBacktestingFilters;
            dispatch(filtersSlice.actions.loadFilters({ data, args }));
          } catch {}
        }
      },
    }),
    getBacktestingRun: build.query<
      BacktestingRunType,
      { backtesting_run_id: number }
    >({
      query: (args) => ({
        url: "get_backtesting_run/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<BacktestingRunType>>
      ) => {
        return response?.data?.data;
      },
      keepUnusedDataFor: 0,
    }),
    getGroupMainCoinsToSignalsCount: build.query<
      GetGroupMainCoinsResponse,
      GetGroupMainCoinsRequest
    >({
      query: (args) => ({
        url: "get_group_main_coins_to_signals_count/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<GetGroupMainCoinsResponse>>
      ) => {
        return response?.data?.data;
      },
    }),
    getGroupMainCoinsToSignalsCountMultipleExchanges: build.query<
      GetGroupMainCoinsResponse,
      GetGroupMainCoinsMultipleExchangesRequest
    >({
      query: (args) => ({
        url: "get_group_main_coins_to_signals_count_multiple_exchanges/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<GetGroupMainCoinsResponse>>
      ) => {
        return response?.data?.data;
      },
    }),
    getBacktestingTradesActivities: build.query<
      BacktestingActivity[],
      { backtesting_run_id: number }
    >({
      query: (args) => ({
        url: "get_backtesting_trades_activities/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<any[]>>
      ) => {
        return convertToObjectsArray(
          response?.data.data,
          response.data.headers
        );
      },
      keepUnusedDataFor: 0,
    }),
    getMinimumBacktestingTimestamp: build.query<
      { minimum_timestamp: number },
      GetMinimumTimestampRequest
    >({
      query: (args) => {
        const queryParams = new URLSearchParams(args as any).toString();
        return {
          url: `get_minimum_backtesting_timestamp/?${queryParams}`,
          method: "GET",
        };
      },
      transformResponse: (
        response: ApiResponse<{ minimum_timestamp: number }>
      ) => response?.data,
    }),
    getBacktestingPeriodicStats: build.query<
      PeriodicStatsDataPoint[],
      { backtesting_run_id: number }
    >({
      query: (args) => ({
        url: "get_backtesting_periodic_stats/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<any[]>>
      ) => {
        return convertToObjectsArray(
          response?.data.data,
          response.data.headers
        );
      },
      keepUnusedDataFor: 0,
    }),
  }),
};
